<script setup lang="ts">
/**
 * Generic table cell that renders an enum value as a small status icon
 * instead of raw text.
 *
 * Callers supply an `iconMap` from enum value → `EnumCellIconSpec` (icon
 * name + optional color + optional tooltip). Lookup is case-insensitive so
 * `"identified"` and `"IDENTIFIED"` resolve identically. Unknown values fall
 * back to `defaultIcon`; missing/blank values show `emptyPlaceholder`.
 *
 * Ships four built-in icon shapes via `EnumCellIconName`:
 * `check`, `check-circle`, `warning-triangle`, `x`.
 *
 * Used in `PaymentsTab` for the payment-status column.
 */
import { computed } from 'vue'

/**
 * Built-in icon names supported by `EnumCell`. Picked to cover the common
 * "status" use cases without pulling in an icon library:
 *
 * - `check`           — bare checkmark / tick (general "yes" / done).
 * - `check-circle`    — filled circle with a white check inside (success / verified).
 * - `warning-triangle`— filled triangle with a white "?" inside (warning / unknown).
 * - `x`               — X mark (error / not done).
 */
export type EnumCellIconName = 'check' | 'check-circle' | 'warning-triangle' | 'x'

export interface EnumCellIconSpec {
  // Per-value icon spec used by `EnumCell.iconMap` and `defaultIcon`.

  icon: EnumCellIconName
  // Built-in icon to draw for this value.

  color?: string
  // CSS color string used as the icon's primary color (defaults to current text color).

  title?: string
  // Tooltip + accessible label; falls back to the raw enum value when absent.
}

/**
 * Generic table cell for enum-like string values that should be rendered as
 * a small status icon (with an optional color) instead of plain text. The
 * caller supplies an `iconMap` from raw enum value to `EnumCellIconSpec`;
 * unknown values fall back to `defaultIcon`, and missing/empty values render
 * `emptyPlaceholder` in the muted theme color.
 *
 * Lookup is case-insensitive after trimming, so backend variants like
 * "identified" or " IDENTIFIED " resolve the same as "IDENTIFIED".
 */
const props = withDefaults(
  defineProps<{
    /** Raw enum value (e.g. payment status from the API). */
    value?: string | null
    /** Map from enum value (case-insensitive) to icon spec. */
    iconMap?: Record<string, EnumCellIconSpec>
    /** Spec used when `value` is present but not in `iconMap`. */
    defaultIcon?: EnumCellIconSpec
    /** Placeholder shown when `value` is missing or blank. */
    emptyPlaceholder?: string
    /** Icon size in CSS units (e.g. `1.2em`, `18px`). Defaults to `1.2em` so it scales with the table font. */
    iconSize?: string
    /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
    maxWidth?: string
    /** Fixed cell width (CSS value). Set by DynamicTable from column config; combined with tightened padding so the icon column shrinks to the icon. */
    width?: string
  }>(),
  {
    value: null,
    iconMap: () => ({}),
    defaultIcon: undefined,
    emptyPlaceholder: '–',
    iconSize: '1.2em',
    maxWidth: undefined,
    width: undefined,
  },
)

const trimmed = computed(() => {
  if (typeof props.value !== 'string') return ''
  return props.value.trim()
})

const hasValue = computed(() => trimmed.value !== '')

/**
 * Builds a normalized lookup for `iconMap` so callers can use the natural
 * enum casing (e.g. `IDENTIFIED`) while we still match values that come back
 * from the API in a different case.
 */
const normalizedIconMap = computed<Record<string, EnumCellIconSpec>>(() => {
  const out: Record<string, EnumCellIconSpec> = {}
  for (const [key, spec] of Object.entries(props.iconMap)) {
    out[key.trim().toUpperCase()] = spec
  }
  return out
})

const resolvedSpec = computed<EnumCellIconSpec | undefined>(() => {
  if (!hasValue.value) return undefined
  const key = trimmed.value.toUpperCase()
  return normalizedIconMap.value[key] ?? props.defaultIcon
})

const accessibleTitle = computed(() => resolvedSpec.value?.title ?? trimmed.value)
</script>

<template>
  <td
    class="font-tufte enum-cell"
    :class="{ 'enum-cell--compact': props.width != null }"
    :style="{
      ...(props.maxWidth ? { maxWidth: props.maxWidth, overflowWrap: 'break-word' } : {}),
      ...(props.width ? { width: props.width } : {}),
    }"
  >
    <template v-if="hasValue && resolvedSpec">
      <span
        class="enum-cell__icon"
        :style="{
          width: props.iconSize,
          height: props.iconSize,
          ...(resolvedSpec.color ? { color: resolvedSpec.color } : {}),
        }"
        role="img"
        :aria-label="accessibleTitle"
        :title="accessibleTitle"
      >
        <!-- Bare checkmark stroke; uses currentColor so it picks up `spec.color`. -->
        <svg
          v-if="resolvedSpec.icon === 'check'"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M5 12.5 L10 17.5 L19 7" />
        </svg>

        <!-- Filled circle with white check inside (success / verified). -->
        <svg
          v-else-if="resolvedSpec.icon === 'check-circle'"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="11" fill="currentColor" />
          <path
            d="M6.5 12.5 L10.5 16.5 L17.5 8.5"
            fill="none"
            stroke="#ffffff"
            stroke-width="2.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <!-- Filled triangle with a white "?" inside (warning / unknown). -->
        <svg
          v-else-if="resolvedSpec.icon === 'warning-triangle'"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            d="M12 2.5 L22.5 21 L1.5 21 Z"
            fill="currentColor"
            stroke="currentColor"
            stroke-width="1.2"
            stroke-linejoin="round"
          />
          <text
            x="12"
            y="18.5"
            text-anchor="middle"
            font-family="Helvetica, Arial, sans-serif"
            font-size="12"
            font-weight="700"
            fill="#ffffff"
          >
            ?
          </text>
        </svg>

        <!-- X mark (error / not done). -->
        <svg
          v-else-if="resolvedSpec.icon === 'x'"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M6 6 L18 18 M18 6 L6 18" />
        </svg>
      </span>
    </template>
    <span v-else class="text-tufte-muted">{{ props.emptyPlaceholder }}</span>
  </td>
</template>

<style scoped>
.enum-cell__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  line-height: 1;
}

.enum-cell__icon svg {
  width: 100%;
  height: 100%;
  display: block;
}

/*
 * When DynamicTable forwards a `width` for this cell, we treat the column as
 * an icon-only gutter: tighten horizontal padding and prevent wrapping so the
 * column actually settles at the requested width instead of being padded out
 * to a "normal" data column.
 */
.enum-cell.enum-cell--compact {
  padding-left: 0.25rem;
  padding-right: 0.25rem;
  white-space: nowrap;
  text-align: center;
}
</style>
